import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Modal } from '../shared/Modal';
import { Field } from '../shared/Field';
import { Input } from '../shared/Input';
import { Select } from '../shared/Select';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Icon } from '../shared/Icons';
import { CopyLink } from './CopyLink';
import { useCreateInvite } from '../../hooks/use-invites';
import { useFederationsQuery } from '../../hooks/use-federations';
import { useSportsQuery } from '../../hooks/use-sports';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';
import type { CreatedInvite } from '../../services/invites.service';

interface FormValues {
  full_name: string;
  email: string;
  role: 'referee' | 'federation_admin';
  federation_id: string;
  sport_id: string;
}

const EMPTY: FormValues = {
  full_name: '',
  email: '',
  role: 'referee',
  federation_id: '',
  sport_id: '',
};

// ProRef's own "add a user to a federation" flow. There is no create-an-account endpoint by
// design — signup is invite-only, so the person always sets their own password from a
// single-use link. What's ProRef-specific here (and why this isn't just the Invites page) is
// that a super_admin is global: it has no federation of its own, so it must SAY which tenant
// and sport the invite lands in. The API requires both from a super_admin and refuses without.
export function InviteUserDialog() {
  const { t } = useTranslation();
  const createInvite = useCreateInvite();
  const [open, setOpen] = useState(false);
  // Set once the invite exists: the panel that replaces the form and shows the one-time link.
  const [created, setCreated] = useState<{ invite: CreatedInvite; emailSent: boolean } | null>(null);

  const { data: federations, isLoading: fedsLoading } = useFederationsQuery(open);
  const { data: sports, isLoading: sportsLoading } = useSportsQuery(open);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: EMPTY });

  // Seed each tenant select with its first option once that catalogue lands — an unset <select>
  // shows the first option but holds '', which the API rejects as a bad uuid.
  //
  // setValue on the two tenant fields only, NOT reset(): the catalogues resolve after the dialog
  // opens (and can resolve again on a background refetch), and a reset would wipe the name and
  // email out from under someone who started typing straight away. Guarded on the field still
  // being empty so it can never overwrite a choice the admin already made.
  useEffect(() => {
    if (!open) return;
    const firstFed = federations?.[0]?.id;
    if (firstFed && !getValues('federation_id')) setValue('federation_id', firstFed);
    const firstSport = sports?.[0]?.id;
    if (firstSport && !getValues('sport_id')) setValue('sport_id', firstSport);
  }, [open, federations, sports, setValue, getValues]);

  function close() {
    setOpen(false);
    setCreated(null);
    // The form outlives the dialog (Modal only stops rendering its children), so clear it here
    // rather than on open — otherwise a cancelled draft reappears the next time it's opened.
    reset(EMPTY);
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      const { invite, emailSent } = await createInvite.mutateAsync(values);
      setCreated({ invite, emailSent });
      // Distinguish "sent" from "created but not emailed" so a silent delivery miss is visible —
      // when the mail didn't go out, the copyable link below is the ONLY way the person gets in.
      if (emailSent) {
        notifySuccess(t('admin.users.inviteDialog.sentToast', { name: invite.full_name }));
      } else {
        notifyWarning(t('admin.users.inviteDialog.sentButEmailFailedToast', { name: invite.full_name }));
      }
    } catch (err) {
      notifyError(err, t('admin.users.inviteDialog.errorToast'));
    }
  });

  const catalogueLoading = fedsLoading || sportsLoading;
  // An invite has to name BOTH a federation and a sport (a super_admin has neither of its own to
  // fall back on), so an empty catalogue on either side means there is nothing valid to submit.
  const noFederations = !fedsLoading && (federations?.length ?? 0) === 0;
  const noSports = !sportsLoading && (sports?.length ?? 0) === 0;

  return (
    <>
      <Button variant="accent" size="sm" onClick={() => setOpen(true)}>
        <Icon.plus className="h-3.5 w-3.5" /> {t('admin.users.inviteDialog.addUser')}
      </Button>

      <Modal
        open={open}
        onClose={createInvite.isPending ? () => {} : close}
        title={created ? t('admin.users.inviteDialog.titleCreated') : t('admin.users.inviteDialog.titleForm')}
        sub={created ? t('admin.users.inviteDialog.subCreated') : t('admin.users.inviteDialog.subForm')}
        footer={
          created ? (
            <Button variant="accent" onClick={close}>
              {t('admin.users.inviteDialog.done')}
            </Button>
          ) : (
            <>
              <Button onClick={close} disabled={createInvite.isPending}>
                {t('common.cancel')}
              </Button>
              <Button
                form="invite-user-form"
                type="submit"
                variant="accent"
                disabled={createInvite.isPending || catalogueLoading || noFederations || noSports}
              >
                {createInvite.isPending ? (
                  <>
                    <Spinner className="w-3.5 h-3.5" /> {t('admin.users.inviteDialog.sending')}
                  </>
                ) : (
                  t('admin.users.inviteDialog.sendInvite')
                )}
              </Button>
            </>
          )
        }
      >
        {created ? (
          <div className="flex flex-col gap-2.5">
            <p className="text-[13px] text-ink-2">
              {created.emailSent
                ? t('admin.users.inviteDialog.emailSent', { email: created.invite.email })
                : t('admin.users.inviteDialog.emailFailed', { email: created.invite.email })}{' '}
              {t('admin.users.inviteDialog.expiresNote', {
                date: new Date(created.invite.expires_at).toLocaleDateString(),
              })}
            </p>
            <CopyLink url={`${window.location.origin}/signup?token=${created.invite.token}`} />
          </div>
        ) : (
          <form id="invite-user-form" onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
            <Field label={t('admin.users.editDialog.fullNameLabel')} htmlFor="invite-user-name" error={errors.full_name?.message}>
              <Input
                id="invite-user-name"
                placeholder={t('admin.users.inviteDialog.fullNamePlaceholder')}
                autoComplete="off"
                {...register('full_name', {
                  required: t('admin.users.editDialog.fullNameRequired'),
                  maxLength: { value: 100, message: t('admin.users.editDialog.fullNameMaxLength') },
                })}
              />
              <p className="mt-1 text-[11px] text-muted">
                {t('admin.users.inviteDialog.fullNameHint')}
              </p>
            </Field>

            <Field label={t('admin.users.inviteDialog.emailLabel')} htmlFor="invite-user-email" error={errors.email?.message}>
              <Input
                id="invite-user-email"
                type="email"
                placeholder={t('admin.users.inviteDialog.emailPlaceholder')}
                {...register('email', { required: t('admin.users.inviteDialog.emailRequired') })}
              />
            </Field>

            <Field label={t('admin.users.editDialog.federationLabel')} htmlFor="invite-user-federation">
              <Select id="invite-user-federation" {...register('federation_id', { required: true })}>
                {(federations ?? []).map((fed) => (
                  <option key={fed.id} value={fed.id}>
                    {fed.name}
                  </option>
                ))}
              </Select>
              {/* A super_admin has no federation of its own, so there is no sensible default to
                  fall back on — without a tenant the API refuses the invite outright. */}
              {noFederations && (
                <p className="mt-1 text-[11px] text-danger">
                  {t('admin.users.inviteDialog.noFederationsError')}
                </p>
              )}
            </Field>

            <Field label={t('admin.users.editDialog.sportLabel')} htmlFor="invite-user-sport">
              <Select id="invite-user-sport" {...register('sport_id', { required: true })}>
                {(sports ?? []).map((sport) => (
                  <option key={sport.id} value={sport.id}>
                    {sport.name}
                  </option>
                ))}
              </Select>
              {noSports && (
                <p className="mt-1 text-[11px] text-danger">
                  {t('admin.users.inviteDialog.noSportsError')}
                </p>
              )}
            </Field>

            <Field label={t('admin.users.editDialog.roleLabel')} htmlFor="invite-user-role">
              <Select id="invite-user-role" {...register('role')}>
                <option value="referee">{t('admin.users.editDialog.roleReferee')}</option>
                <option value="federation_admin">{t('admin.users.editDialog.roleFederationAdmin')}</option>
              </Select>
            </Field>
          </form>
        )}
      </Modal>
    </>
  );
}
